import { useId, useMemo, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ChevronRight, Download, Search } from 'lucide-react';
import type { SalaryBand } from '../../services/salaryBandsApi';
import { downloadSalaryBandsCsv } from './salaryBandsCsv';
import { groupBandLadders, ladderOpen } from './salaryBandRows';

const euro = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

function money(value: number) {
  return Number.isFinite(value) ? euro.format(value) : '—';
}

// Title is the column that names the row, so it stays put while the figures
// scroll under it. It is FIRST because a pinned column further right would need
// a left offset equal to the columns before it, which changes with their text.
// It needs an opaque background or the scrolled cells show through, and the
// right-hand rule because border-collapse drops borders on sticky cells.
const PINNED =
  'sticky left-0 min-w-[10rem] max-w-[16rem] shadow-[1px_0_0_0_theme(colors.pearl)]';

// A thin rule where each column group starts, so nine figure columns read as
// four groups. An inset shadow, because border-collapse drops borders on the
// sticky header.
const GROUP_EDGE = 'shadow-[inset_1px_0_0_0_theme(colors.pearl)]';
// Fixed widths for `alignWith` tables, keyed by the sub-header text.
const COLUMN_WIDTH: Record<string, string> = {
  Min: 'w-28',
  Avg: 'w-28',
  Max: 'w-28',
  Lower: 'w-20',
  Middle: 'w-20',
  Upper: 'w-20',
  'Initial grant': 'w-28',
  VSOP: 'w-24',
  'Rating grant': 'w-28',
};
const NUM = 'whitespace-nowrap px-2.5 py-2 text-right tabular-nums';

// The proxy reads an empty OTE cell as 0, so a band without variable pay
// rendered "0 €" — a number nobody earns. No OTE is a dash, not a zero.
function optionalMoney(value: number) {
  return Number.isFinite(value) && value > 0 ? euro.format(value) : '—';
}

export function SalaryBandsTable({
  bands,
  showThirds = false,
  showRating = true,
  title,
  onRating,
  allowDownload = false,
  searchable = false,
  highlight,
  alignWith,
}: {
  bands: SalaryBand[];
  showThirds?: boolean;
  showRating?: boolean;
  title?: string;
  onRating?: (band: SalaryBand) => void;
  allowDownload?: boolean;
  searchable?: boolean;
  /** Rows whose band identifier is listed get a tint and the label (e.g. the viewer's own band). */
  highlight?: { identifiers: string[]; label: string };
  /**
   * For tables stacked on one page (My salary bands: current, next, career path). The optional
   * columns are decided from THESE bands instead of the table's own, and the columns get fixed
   * widths, so every table that passes the same set lays out identically and the figures line up.
   */
  alignWith?: SalaryBand[];
}) {
  const [search, setSearch] = useState('');
  const searchInputId = useId();
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return bands;
    return bands.filter((band) =>
      [
        band.identifier,
        band.department,
        band.unit,
        band.careerPath,
        band.title,
        band.level,
        `L${band.level}`,
        band.degree,
      ]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [bands, search]);
  const columnSource = alignWith ?? bands;
  const hasVsop = columnSource.some((band) => typeof band.vsop === 'number');
  // Judged on ALL bands, not the filtered ones, so columns do not appear and
  // disappear while someone types into the filter. Most departments have no
  // OTE and no degree split; six empty columns are what pushed the table past
  // a laptop's width (Florian Drabeck, Sep 29 2026).
  const hasOte = columnSource.some((band) => band.oteMin > 0 || band.oteAverage > 0 || band.oteMax > 0);
  const hasDegree = columnSource.some((band) => Boolean(band.degree));
  const highlightIds = highlight?.identifiers;
  const ladders = useMemo(() => groupBandLadders(visible, highlightIds), [visible, highlightIds]);
  // Ladder rows only earn their place when there are several ladders to choose between. A table
  // that IS one ladder (my career path, a colleague's) is already named by its heading, so a
  // header row there is noise and a collapse button nobody needs. Judged on all bands, so a
  // filter narrowing the company table to one role does not make the headers vanish.
  const multiLadder = useMemo(() => groupBandLadders(bands).length > 1, [bands]);
  // Ladders someone clicked, relative to the default; `allOpen` is set by
  // Expand all / Collapse all and replaces the default for every ladder.
  const [toggled, setToggled] = useState<ReadonlySet<string>>(() => new Set());
  const [allOpen, setAllOpen] = useState<boolean | null>(null);
  const openState = { totalBands: bands.length, filtering: search.trim() !== '', toggled, allOpen };
  const anyClosed = ladders.some((ladder) => !ladderOpen(ladder, openState));
  const toggleLadder = (key: string) =>
    setToggled((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setAll = (open: boolean) => {
    setAllOpen(open);
    setToggled(new Set());
  };
  // The identity columns span both header rows; everything after them sits
  // under a group heading. Built here so the group row's colSpans and the
  // column headers cannot disagree about which optional columns exist.
  const groups = [
    { label: 'Base salary', columns: ['Min', 'Avg', 'Max'], srPrefix: '' },
    ...(hasOte ? [{ label: 'OTE', columns: ['Min', 'Avg', 'Max'], srPrefix: 'OTE ' }] : []),
    ...(showThirds ? [{ label: 'Thirds', columns: ['Lower', 'Middle', 'Upper'], srPrefix: '', suffix: ' third' }] : []),
    {
      label: 'Equity',
      columns: ['Initial grant', ...(hasVsop ? ['VSOP'] : []), ...(showRating ? ['Rating grant'] : [])],
      srPrefix: '',
    },
  ];

  // Every column after the pinned title: Level, Degree, then the figures.
  const columnCount = 1 + (hasDegree ? 1 : 0) + groups.reduce((sum, group) => sum + group.columns.length, 0);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {title ? <h2 className="text-base font-semibold text-black">{title}</h2> : <span />}
        <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
          {searchable && bands.length > 1 ? (
            <div className="flex w-full items-center gap-3 sm:w-auto">
              <label
                htmlFor={searchInputId}
                className="shrink-0 text-sm font-semibold text-black"
              >
                Filter bands
              </label>
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-iron" />
                <Input
                  id={searchInputId}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Business Automation, L3, MA…"
                  className="!h-10 !min-h-10 !pl-9"
                />
              </div>
            </div>
          ) : null}
          {multiLadder ? (
            <Button variant="ghost" size="sm" onClick={() => setAll(anyClosed)}>
              {anyClosed ? 'Expand all' : 'Collapse all'}
            </Button>
          ) : null}
          {allowDownload ? (
            <Button variant="secondary" onClick={() => downloadSalaryBandsCsv(visible, 'recruiting-salary-bands.csv')}>
              <Download className="mr-2 h-4 w-4" />
              Download CSV
            </Button>
          ) : null}
        </div>
      </div>

      {/* The table scrolls inside its own box, capped at the viewport, so the
          header row can stay pinned (sticky needs the scrolling ancestor to be
          this box) and the horizontal scrollbar is on screen instead of at the
          bottom of a 500-row list. `isolate` keeps the pinned cells' z-indices
          INSIDE this box: without it the sticky header (z-20) competed with the
          page, and the design-system Autocomplete above the table (not portaled,
          `z-overlay` = 10) opened its options underneath the Title column. */}
      <div
        data-testid="salary-bands-scroll"
        className="isolate max-h-[calc(100vh-8rem)] overflow-auto border border-pearl bg-white"
      >
        <table
          className={`border-collapse text-left text-sm ${alignWith ? 'w-full table-fixed' : 'min-w-full'}`}
        >
          {alignWith ? (
            <colgroup>
              <col className="w-72" />
              <col className="w-16" />
              {hasDegree ? <col className="w-20" /> : null}
              {groups.flatMap((group) =>
                group.columns.map((column) => <col key={`${group.label}-${column}`} className={COLUMN_WIDTH[column]} />),
              )}
            </colgroup>
          ) : null}
          <thead className="sticky top-0 z-10 bg-snow text-xs font-semibold text-iron shadow-[0_1px_0_0_theme(colors.pearl)]">
            <tr>
              <th rowSpan={2} className={`${PINNED} z-20 bg-snow px-2.5 py-2 align-bottom`}>Title</th>
              <th rowSpan={2} className="px-2.5 py-2 align-bottom">Level</th>
              {hasDegree ? <th rowSpan={2} className="px-2.5 py-2 align-bottom">Degree</th> : null}
              {groups.map((group) => (
                <th
                  key={group.label}
                  colSpan={group.columns.length}
                  scope="colgroup"
                  className={`${GROUP_EDGE} px-2.5 pb-0 pt-2 text-center text-black`}
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              {groups.flatMap((group) =>
                group.columns.map((column, index) => (
                  <th
                    key={`${group.label}-${column}`}
                    scope="col"
                    className={`${index === 0 ? GROUP_EDGE : ''} px-2.5 pb-2 pt-1 align-bottom ${
                      column === 'Rating grant' ? '' : 'text-right'
                    }`}
                  >
                    {group.srPrefix ? <span className="sr-only">{group.srPrefix}</span> : null}
                    {column}
                    {group.suffix ? <span className="sr-only">{group.suffix}</span> : null}
                  </th>
                )),
              )}
            </tr>
          </thead>
          {ladders.map((ladder) => {
            const open = !multiLadder || ladderOpen(ladder, openState);
            const levels =
              ladder.minLevel === ladder.maxLevel ? `L${ladder.minLevel}` : `L${ladder.minLevel}–L${ladder.maxLevel}`;
            return (
              <tbody key={ladder.key} data-testid="salary-ladder">
                {/* The ladder row is a disclosure, so it is a plain <button>
                    filling the pinned cell rather than the DS Button: a
                    full-width row label, same shape as the cockpit's
                    disclosure rows. Its label sits in the pinned cell so it
                    stays readable while the figures scroll sideways. */}
                {multiLadder ? (
                <tr className="border-t border-pearl bg-snow">
                  <th scope="rowgroup" className={`${PINNED} z-[1] bg-snow p-0 text-left`}>
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggleLadder(ladder.key)}
                      className="flex w-full items-start gap-1.5 px-2.5 py-2 text-left hover:bg-pearl/40"
                    >
                      <ChevronRight
                        aria-hidden
                        className={`mt-0.5 h-4 w-4 shrink-0 text-iron transition-transform ${open ? 'rotate-90' : ''}`}
                      />
                      <span>
                        <span className="block font-semibold text-black">{ladder.role}</span>
                        <span className="block text-xs font-normal text-iron">
                          {[ladder.careerPath, ladder.department].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </span>
                    </button>
                  </th>
                  <td colSpan={columnCount} className="px-2.5 py-2 align-top text-xs text-iron">
                    {levels} · {ladder.rows.length} {ladder.rows.length === 1 ? 'band' : 'bands'}
                    {ladder.hasHighlight && highlight && !open ? ` · ${highlight.label} inside` : ''}
                  </td>
                </tr>
                ) : null}
                {open
                  ? ladder.rows.map(({ band, highlighted }) => {
                      const tint = highlighted ? 'bg-lightBlue' : 'bg-white group-hover:bg-snow';
                      return (
                        <tr
                          key={band.id}
                          data-highlighted={highlighted || undefined}
                          className={`group border-t border-pearl/60 align-top ${
                            highlighted ? 'bg-lightBlue' : 'hover:bg-snow'
                          }`}
                        >
                          <td className={`${PINNED} z-[1] py-2 ${multiLadder ? 'pl-8' : 'pl-2.5'} pr-2.5 font-semibold text-black ${tint}`}>
                            {band.title || '—'}
                            {highlighted && highlight ? (
                              <span className="mt-0.5 block text-xs font-medium text-trustedBlue">{highlight.label}</span>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-2.5 py-2 tabular-nums">
                            {band.level ? `L${band.level}` : '—'}
                          </td>
                          {hasDegree ? <td className="px-2.5 py-2">{band.degree || '—'}</td> : null}
                          <td className={`${GROUP_EDGE} ${NUM}`}>{money(band.min)}</td>
                          <td className={`${NUM} font-semibold text-black`}>{money(band.average)}</td>
                          <td className={NUM}>{money(band.max)}</td>
                          {hasOte ? <td className={`${GROUP_EDGE} ${NUM}`}>{optionalMoney(band.oteMin)}</td> : null}
                          {hasOte ? (
                            <td className={`${NUM} font-semibold text-black`}>{optionalMoney(band.oteAverage)}</td>
                          ) : null}
                          {hasOte ? <td className={NUM}>{optionalMoney(band.oteMax)}</td> : null}
                          {showThirds ? <td className={`${GROUP_EDGE} ${NUM}`}>{band.lowerThird || '—'}</td> : null}
                          {showThirds ? <td className={NUM}>{band.middleThird || '—'}</td> : null}
                          {showThirds ? <td className={NUM}>{band.upperThird || '—'}</td> : null}
                          <td className={`${GROUP_EDGE} ${NUM}`}>{money(band.grantValueVsop)}</td>
                          {hasVsop ? <td className={NUM}>{band.vsop?.toLocaleString('de-DE') ?? '—'}</td> : null}
                          {showRating ? (
                            <td className="px-2.5 py-1">
                              <Button variant="ghost" size="sm" onClick={() => onRating?.(band)}>
                                View
                              </Button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  : null}
              </tbody>
            );
          })}
        </table>
        {visible.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-iron">No salary bands match this filter.</p>
        ) : null}
      </div>
    </section>
  );
}

export function SalaryBandsSkeleton() {
  return (
    <div aria-label="Loading salary bands" className="space-y-3">
      <div className="h-10 w-64 animate-pulse bg-cotton" />
      <div className="space-y-px border border-pearl bg-pearl">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="h-12 animate-pulse bg-cotton" />
        ))}
      </div>
    </div>
  );
}
